import { CommonModule, DecimalPipe } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { OverlayModule } from '@angular/cdk/overlay';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { SgMaterialAllModule } from '@sg/material-all';
import {
  LendingRateLabelComponent,
  SgAccordionComponent,
  SgAsyncContainerComponent,
  SgAutoCompleteSelect2Component,
  SgAutoCompleteSelect2OptionDirective,
  SgAutoCompleteSelect2TriggerDirective,
  SgAutoCompleteSelect2AsyncTemplateDirective,
  SgAvatarComponent,
  SgBlockableComponent,
  SgButtonComponent,
  SgCheckboxComponent,
  SgCopyButtonComponent,
  SgCounterBadgeComponent,
  SgDateRangeComponent,
  SgDialogComponent,
  SgDropdownChipsListComponent,
  SgDropdownComponent,
  SgDropdown2Component,
  SgDropdownOptionItemComponent,
  SgEmptyStateComponent,
  SgFlexDividerComponent,
  SgInputComponent,
  SgInput2Component,
  SgLabelComponent,
  SgLinkComponent,
  SgMenuComponent,
  SgPieChartComponent,
  SgRadioButtonComponent,
  SgSliderComponent,
  SgStatusTagComponent,
  SgTableComponent,
  SgTextareaComponent,
  SgTextComponent,
  SgToastComponent,
  SgToastService,
  SgToggleComponent,
  SgDividerComponent,
  SgPillSwitcherComponent,
  SgInfoTooltipComponent,
  SgLogoLoaderComponent,
} from './components';
import {
  InlineSvgDirective,
  NgEventsDirective,
  SgCopyOnHoverDirective,
  SgFileInputDirective,
  SgFlexBreakRowDirective,
  SgSkeletonDirective,
  SgSwitchIconOnHoverDirective,
  SgTableHeaderSkeletonDirective,
  SgTooltipDirective,
  VarDirective,
} from './directives';
import { SgDocTitleDirective, SgDocTitlePipe } from './doc-title';
import {
  AbbrPipe,
  FloorIfAboveOnePipe,
  FromNowPipe,
  RoundIfAboveOnePipe,
  SgAbsPipe,
  SgArrayGetPipe,
  SgBadgeLimit,
  SgBpsToRateGroupPipe,
  SgCapitalizeWordsPipe,
  SgConsolePipe,
  SgCountryPipe,
  SgCurrencyPipe,
  SgCurrencySymbolPipe,
  SgFloorPipe,
  SgHumanFileSizePipe,
  SgHumanNumPipe,
  SgJoinPipe,
  SgMapGetPipe,
  SgNegatePipe,
  SgObjectGetPipe,
  SGRelativeDatePipe,
  SgSplitPipe,
  SgToNoCasePipe,
  SgTrimPipe,
  BpsToRoundedPercentsPipe,
  SgEntityCountPipe,
  SgUnitsCountPipe,
  SgRatePercentPipe,
  SgRatioToPercentagePipe,
  SgSmartCurrencyPipe,
} from './pipes';

const PIPES = [
  AbbrPipe,
  FromNowPipe,
  SgCurrencyPipe,
  SgCurrencySymbolPipe,
  SgConsolePipe,
  SgNegatePipe,
  SgToNoCasePipe,
  SgHumanNumPipe,
  SgFloorPipe,
  SgHumanFileSizePipe,
  SgCapitalizeWordsPipe,
  SgMapGetPipe,
  SgObjectGetPipe,
  SgDocTitlePipe,
  SgCountryPipe,
  SgJoinPipe,
  SgSplitPipe,
  SgTrimPipe,
  SgBadgeLimit,
  SgArrayGetPipe,
  SgBpsToRateGroupPipe,
  SgAbsPipe,
  FloorIfAboveOnePipe,
  RoundIfAboveOnePipe,
  SGRelativeDatePipe,
  BpsToRoundedPercentsPipe,
  SgEntityCountPipe,
  SgUnitsCountPipe,
  SgRatePercentPipe,
  SgRatioToPercentagePipe,
  SgSmartCurrencyPipe,
];

const DIRECTIVES = [
  SgFlexBreakRowDirective,
  VarDirective,
  NgEventsDirective,
  SgDocTitleDirective,
  SgCopyOnHoverDirective,
  SgFileInputDirective,
  SgSwitchIconOnHoverDirective,
  SgTooltipDirective,
  SgSkeletonDirective,
  SgTableHeaderSkeletonDirective,
  InlineSvgDirective,
];

const COMPONENTS = [
  SgButtonComponent,
  SgAccordionComponent,
  SgTextComponent,
  SgTextareaComponent,
  SgRadioButtonComponent,
  SgAvatarComponent,
  SgDropdownComponent,
  SgDropdown2Component,
  SgDateRangeComponent,
  SgAutoCompleteSelect2Component,
  SgAutoCompleteSelect2OptionDirective,
  SgAutoCompleteSelect2TriggerDirective,
  SgAutoCompleteSelect2AsyncTemplateDirective,
  SgInputComponent,
  SgInput2Component,
  SgStatusTagComponent,
  SgLabelComponent,
  SgLinkComponent,
  LendingRateLabelComponent,
  SgDropdownOptionItemComponent,
  SgDropdownChipsListComponent,
  SgCheckboxComponent,
  SgMenuComponent,
  SgPieChartComponent,
  SgTableComponent,
  SgSliderComponent,
  SgToggleComponent,
  SgToastComponent,
  SgCounterBadgeComponent,
  SgEmptyStateComponent,
  SgCopyButtonComponent,
  SgDividerComponent,
  SgDialogComponent,
  SgAsyncContainerComponent,
  SgFlexDividerComponent,
  SgBlockableComponent,
  SgPillSwitcherComponent,
  SgInfoTooltipComponent,
  SgLogoLoaderComponent,
];

@NgModule({
  declarations: [PIPES, DIRECTIVES, COMPONENTS],
  imports: [
    CommonModule,
    RouterModule,
    SgMaterialAllModule,
    OverlayModule,
    MatRadioModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    MatSnackBarModule,
    MatDialogModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatProgressSpinnerModule,
  ],
  exports: [PIPES, DIRECTIVES, COMPONENTS],
  providers: [SgToastService, DecimalPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SgCoreModule {}
